import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import globalStyles from './styles/outline-media-gallery.global.pcss?inline';
import innerStyles from './styles/outline-media-gallery.inner.pcss?inline';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '../outline-image/outline-image';
import '../outline-video/outline-video';
import '../outline-modal/outline-modal';
import '../outline-cta/outline-cta';
import '../outline-icon/outline-icon';
import type { OutlineVideo } from '../outline-video/outline-video';

export type MediaKind = 'image' | 'video';

export interface MediaItem {
  id: string | number;
  kind: MediaKind;
  alt?: string;
  src?: string;
  // Video specific
  videoEmbedHtml?: string; // iframe embed HTML (Youtube/Vimeo)
  thumbnailSrc?: string; // optional thumbnail for video
  transcriptLinkHtml?: string; // optional transcript link markup
  caption?: string;
  title?: string;
}

/**
 * outline-media-gallery
 * Renders a responsive grid of media (images and remote videos) with a modal lightbox.
 * - Displays up to initialCount items, with a load-more CTA exposing more in increments.
 */
@customElement('outline-media-gallery')
export class OutlineMediaGallery extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @query('outline-modal') private _modal!: HTMLElement & { showModal?: () => void; closeModal?: () => void };

  // All items provided to the gallery
  @property({ type: Array }) items: MediaItem[] = [];

  // Initial number of items rendered
  @property({ type: Number, attribute: 'initial-count' }) initialCount = 24;

  // How many to reveal per click
  @property({ type: Number, attribute: 'page-size' }) pageSize = 12;

  // Labels for navigation buttons in the modal
  @property({ type: String, attribute: 'prev-label' }) prevLabel = 'Prev';
  @property({ type: String, attribute: 'next-label' }) nextLabel = 'Next';

  // Track how many items are currently visible
  @state() private _visibleCount = 0;

  // Modal state
  @state() private _lightboxOpen = false;
  @state() private _activeIndex: number | null = null;

  private _documentKeyHandler = (e: KeyboardEvent) => this._onKeyDown(e);

  connectedCallback(): void {
    super.connectedCallback();

    this._initializeFromDataAttribute();

    this._visibleCount = Math.min(this.initialCount, this.items.length);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._documentKeyHandler);
  }

  private _initializeFromDataAttribute(): void {
    const dataItems = this.getAttribute('data-items');
    if (dataItems) {
      try {
        const parsedItems = JSON.parse(dataItems);
        if (Array.isArray(parsedItems) && parsedItems.length > 0) {
          this.items = [...parsedItems];
          console.log('Media Gallery: Loaded items from data-items attribute:', this.items.length);
        }
      } catch (e) {
        console.error('Media Gallery: Failed to parse data-items attribute:', e);
      }
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('items') || changed.has('initialCount')) {
      this._visibleCount = Math.min(this.initialCount, this.items.length);
    }
    if (changed.has('_lightboxOpen')) {
      if (this._lightboxOpen) {
        document.addEventListener('keydown', this._documentKeyHandler);
      } else {
        document.removeEventListener('keydown', this._documentKeyHandler);
      }
    }
  }

  private _openLightbox(index: number) {
    this._activeIndex = index;
    this._lightboxOpen = true;
    // Add a body class when the modal opens
    document.body.classList.add('has-outline-media-gallery-open');
    // Need to defer this slightly to ensure modal is in the DOM
    requestAnimationFrame(() => {
      if (this._modal && this._modal.showModal) {
        this._modal.showModal();
      }
    });
  }

  private _closeLightbox(fromModal = false) {
    this._pauseActiveVideos();
    if (!fromModal) {
      this._modal?.closeModal?.();
    }
    this._lightboxOpen = false;
    this._activeIndex = null;
    document.body.classList.remove('has-outline-media-gallery-open');
  }

  private _showMore() {
    const remaining = this.items.length - this._visibleCount;
    const increment = Math.min(this.pageSize, remaining);
    this._visibleCount += increment;
  }

  private _showMoreLabel(): string {
    const remaining = this.items.length - this._visibleCount;
    const toShow = Math.min(this.pageSize, remaining);
    // Show count if there are more items
    if (remaining > this.pageSize) {
      return `Load ${this.pageSize} more`;
    } else if (remaining > 0) {
      return `Show ${toShow} more`;
    }
    return '';
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (!this._lightboxOpen || this._activeIndex == null) return;
    if (e.key === 'Escape') {
      this._closeLightbox();
    } else if (e.key === 'ArrowRight') {
      this._next();
    } else if (e.key === 'ArrowLeft') {
      this._prev();
    }
  }

  private _prev() {
    if (this._activeIndex == null) return;
    this._pauseActiveVideos();
    this._activeIndex = (this._activeIndex - 1 + this.items.length) % this.items.length;
  }

  private _next() {
    if (this._activeIndex == null) return;
    this._pauseActiveVideos();
    this._activeIndex = (this._activeIndex + 1) % this.items.length;
  }

  private _pauseActiveVideos() {
    if (!this._modal) return;

    this._modal
      .querySelectorAll('outline-video')
      .forEach(el => {
        const video = el as OutlineVideo;
        if (typeof video.resetPlayer === 'function') {
          video.resetPlayer();
        }
      });
  }

  private _handleModalClosed = () => {
    if (!this._lightboxOpen) return;
    this._closeLightbox(true);
  };

  private _renderThumbnail(item: MediaItem, index: number): TemplateResult {
    const isVideo = item.kind === 'video';
    const ariaLabel = isVideo ? 'Open video' : 'Open image';

    return html`
      <button
        class="gallery-item"
        type="button"
        @click=${() => this._openLightbox(index)}
        aria-label=${ariaLabel}
      >
        ${isVideo
          ? html`
              <div class="thumb thumb-video">
                ${item.thumbnailSrc
                  ? html`<img src="${item.thumbnailSrc}" alt="${item.alt || ''}" loading="lazy" />`
                  : html`<div class="thumb-fallback" aria-hidden="true"></div>`}
                <span class="play-indicator" aria-hidden="true"></span>
              </div>
            `
          : html`
              <div class="thumb">
                ${item.src
                  ? html`<img src="${item.src}" alt="${item.alt || ''}" loading="lazy" />`
                  : null}
              </div>
            `}
      </button>
    `;
  }

  private _renderLightbox(): TemplateResult | null {
    if (!this._lightboxOpen || this._activeIndex == null) return null;

    const current = this.items[this._activeIndex];
    const transcriptLink = this._renderTranscriptLink(current.transcriptLinkHtml);

    // Render video or image based on media type
    let mediaTemplate;
    if (current.kind === 'video') {
      mediaTemplate = html`<div class="media-frame">
            <outline-video class="on-dark">
              ${current.videoEmbedHtml ? html`${unsafeHTML(current.videoEmbedHtml)}` : null}
              ${current.thumbnailSrc ? html`<img slot="thumbnail" src="${current.thumbnailSrc}" alt="${current.alt || ''}" />` : null}
              ${transcriptLink}
            </outline-video>
          </div>`;
    } else {
      mediaTemplate = html`<div class="media-frame">
            ${current.src ? html`<img class="lightbox-media" src="${current.src}" alt="${current.alt || ''}" />` : null}
          </div>`;
    }

  // Shell wrapper so the title and media stay lined up.
    const shellClass = current.kind === 'video'
      ? 'media-shell media-shell--video'
      : 'media-shell media-shell--image';

    return html`
      <outline-modal
        trap-focus="true"
        style="--outline-modal--backdrop-color: rgba(0,0,0,0.9)"
        transparent
        @outline-modal-closed=${this._handleModalClosed}
      >
        <button slot="close-button" class="close-button" aria-label="Close">
          <outline-icon icon-classes="fa-solid fa-xmark" color='var(--text-brand-secondary)'></outline-icon>
        </button>

        <div class="lightbox-wrapper">
          <div class="lightbox-content" tabindex="0">
            <div class="media-container">
              <div class=${shellClass}>
                ${current.title ? html`<h3 class="media-title">${current.title}</h3>` : null}
                ${mediaTemplate}
              </div>
            </div>
          </div>

          <div class="lightbox-controls">
            <button class="nav prev" @click=${this._prev} aria-label="Previous">
              <outline-icon icon-classes="fa-solid fa-chevron-left"></outline-icon>
              <span class="nav-text">${this.prevLabel}</span>
            </button>
            <button class="nav next" @click=${this._next} aria-label="Next">
              <span class="nav-text">${this.nextLabel}</span>
              <outline-icon icon-classes="fa-solid fa-chevron-right"></outline-icon>
            </button>
          </div>

          ${current.caption ? html`<div class="media-caption-wrapper">
            <div class="media-caption">${unsafeHTML(current.caption)}</div>
          </div>` : null}
        </div>
      </outline-modal>
    `;
  }

  private _onTouchStartX: number | null = null;
  private _onTouchStartY: number | null = null;
  private _onTouchStartTime: number | null = null;

  private _handleTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    this._onTouchStartX = t.clientX;
    this._onTouchStartY = t.clientY;
    this._onTouchStartTime = Date.now();
  };

  private _renderTranscriptLink(transcriptLinkHtml?: string): TemplateResult | null {
    if (!transcriptLinkHtml) return null;

    const trimmed = transcriptLinkHtml.trim();
    if (!trimmed) return null;

    const containsOutlineCta = /<\s*outline-cta\b/i.test(trimmed);

    const content = containsOutlineCta
      ? unsafeHTML(trimmed)
      : html`<outline-cta variant="link" dark class="media-transcript-link-cta">
          ${unsafeHTML(trimmed)}
        </outline-cta>`;

    return html`<div slot="transcript-link" class="media-transcript-link">
      ${content}
    </div>`;
  }

  private _handleTouchEnd = (e: TouchEvent) => {
    if (this._onTouchStartX == null || this._onTouchStartY == null) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - this._onTouchStartX;
    const deltaY = touch.clientY - this._onTouchStartY;
    const deltaTime = Date.now() - (this._onTouchStartTime || 0);

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Check if it's a horizontal swipe (fast enough and mostly horizontal)
    if (deltaTime < 600 && absX > 30 && absY < 50) {
      if (deltaX < 0) {
        this._next();
      } else {
        this._prev();
      }
    }

    // Reset touch tracking
    this._onTouchStartX = this._onTouchStartY = this._onTouchStartTime = null;
  };

  render(): TemplateResult {
    const hasMore = this.items.length > this._visibleCount;
    const visibleItems = this.items.slice(0, this._visibleCount);

    return html`
      <div class="outline-media-gallery" @touchstart=${this._handleTouchStart} @touchend=${this._handleTouchEnd}>
        <div class="grid">
          ${visibleItems.map((item, idx) => this._renderThumbnail(item, idx))}
        </div>

        ${hasMore
          ? html`
              <div class="load-more">
                <outline-cta variant="link">
                  <button type="button" @click=${this._showMore}>
                    ${this._showMoreLabel()}
                  </button>
                </outline-cta>
              </div>
            `
          : null}

        ${this._renderLightbox()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-media-gallery': OutlineMediaGallery;
  }
}
